import {
  CONSOLE_ROUTES,
  type ConsoleApiKey,
  ConsoleApiKeyCreatedSchema,
  ConsoleConversationListSchema,
  ConsoleConversationTraceSchema,
  ConsoleEvalCaseSavedSchema,
  ConsoleHistorySchema,
  ConsoleIntegrationsSchema,
  ConsoleInviteCreatedSchema,
  type ConsoleMcpServer,
  ConsoleMcpServerSchema,
  ConsoleSettingsSchema,
  ConsoleTeamSchema,
  type ConsoleTool,
  ConsoleToolHealthSchema,
  ConsoleToolsSchema,
  type TenantId,
} from '@plato/shared';
import type { ConsoleData } from './console-data.ts';
import { httpEvalsData } from './evals-data.ts';
import { callConsoleApi, sendToConsoleApi } from './http-client.ts';
import { httpUsageData } from './http-usage.ts';
import type { ApiKeyRow, McpServerRow, NewMcpServer, ToolRow } from './rows.ts';
import { httpSkillsData } from './skills-data.ts';

const integrations = (tenant: TenantId) =>
  callConsoleApi(CONSOLE_ROUTES.integrations(tenant), ConsoleIntegrationsSchema);

export const httpConsoleData: ConsoleData = {
  ...httpUsageData,
  ...httpEvalsData,
  ...httpSkillsData,
  demo: false,

  getAssistantSettings: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.settings(tenant), ConsoleSettingsSchema)).settings,

  updateAssistantSettings: async (tenant, changes) =>
    (
      await callConsoleApi(CONSOLE_ROUTES.settings(tenant), ConsoleSettingsSchema, {
        method: 'PATCH',
        body: { settings: changes },
      })
    ).settings,

  listTools: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.tools(tenant), ConsoleToolsSchema)).data.map(toolRow),

  updateTool: (tenant, source, tool, change) =>
    sendToConsoleApi(CONSOLE_ROUTES.tool(tenant, source, tool), { method: 'PATCH', body: change }),

  resetTool: (tenant, source, tool) =>
    sendToConsoleApi(CONSOLE_ROUTES.tool(tenant, source, tool), { method: 'DELETE' }),

  getIntegrationSetup: async (tenant) => {
    const value = await integrations(tenant);
    const publishable = value.api_keys.filter(
      (key) => key.kind === 'publishable' && key.revoked_at === null,
    );
    return {
      apiOrigin: value.api_origin,
      embedOrigin: value.embed_origin,
      publishableKey: publishable.at(-1)?.publishable_key ?? null,
      allowedOrigins: value.allowed_origins,
      mcpServers: value.mcp_servers.map(mcpServerRow),
    };
  },

  listApiKeys: async (tenant) => (await integrations(tenant)).api_keys.map(apiKeyRow),

  setAllowedOrigins: (tenant, origins) =>
    sendToConsoleApi(CONSOLE_ROUTES.origins(tenant), { method: 'PUT', body: { origins } }),

  updateMcpServer: (tenant, name, change) =>
    sendToConsoleApi(CONSOLE_ROUTES.mcpServer(tenant, name), { method: 'POST', body: change }),

  createMcpServer: async (tenant, server) =>
    mcpServerRow(
      await callConsoleApi(CONSOLE_ROUTES.mcpServers(tenant), ConsoleMcpServerSchema, {
        method: 'POST',
        body: mcpServerCreateRequest(server),
      }),
    ),

  deleteMcpServer: (tenant, name) =>
    sendToConsoleApi(CONSOLE_ROUTES.mcpServer(tenant, name), { method: 'DELETE' }),

  createApiKey: async (tenant, kind) => {
    const created = await callConsoleApi(
      CONSOLE_ROUTES.apiKeys(tenant),
      ConsoleApiKeyCreatedSchema,
      {
        method: 'POST',
        body: { kind },
      },
    );
    return { keyId: created.key.id, value: created.value };
  },

  revokeApiKey: (tenant, keyId) =>
    sendToConsoleApi(CONSOLE_ROUTES.apiKey(tenant, keyId), { method: 'DELETE' }),

  getTeam: async (tenant) => {
    const team = await callConsoleApi(CONSOLE_ROUTES.team(tenant), ConsoleTeamSchema);
    return {
      members: team.members.map((member) => ({
        id: member.account_id,
        name: member.name ?? member.email ?? member.account_id,
        email: member.email,
        role: member.role,
        joinedAt: member.joined_at,
      })),
      invites: team.invites.map((invite) => ({
        id: invite.id,
        email: invite.email,
        role: invite.role,
        expiresAt: invite.expires_at,
      })),
    };
  },

  inviteMember: async (tenant, email, role) => {
    const created = await callConsoleApi(
      CONSOLE_ROUTES.teamInvites(tenant),
      ConsoleInviteCreatedSchema,
      {
        method: 'POST',
        body: { email, role },
      },
    );
    return { inviteId: created.invite.id, link: created.link };
  },

  revokeInvite: (tenant, inviteId) =>
    sendToConsoleApi(CONSOLE_ROUTES.teamInvite(tenant, inviteId), { method: 'DELETE' }),

  setMemberRole: (tenant, accountId, role) =>
    sendToConsoleApi(CONSOLE_ROUTES.teamMember(tenant, accountId), {
      method: 'POST',
      body: { role },
    }),

  removeMember: (tenant, accountId) =>
    sendToConsoleApi(CONSOLE_ROUTES.teamMember(tenant, accountId), { method: 'DELETE' }),

  listAuditEvents: async (tenant) =>
    (await callConsoleApi(CONSOLE_ROUTES.history(tenant), ConsoleHistorySchema)).data.map(
      (entry) => ({
        id: entry.id,
        at: entry.at,
        actor: entry.actor.label,
        event: entry.action,
        details: entry.details,
      }),
    ),

  listConversations: (tenant, filter) =>
    callConsoleApi(
      `${CONSOLE_ROUTES.conversations(tenant)}${queryString(filter)}`,
      ConsoleConversationListSchema,
    ),

  getConversationTrace: (tenant, conversation, user) =>
    callConsoleApi(
      CONSOLE_ROUTES.conversationTrace(tenant, conversation, user),
      ConsoleConversationTraceSchema,
    ),

  saveReplyAsEvalCase: async (tenant, conversation, reply, user) =>
    (
      await callConsoleApi(
        CONSOLE_ROUTES.conversationEvalCase(tenant, conversation, reply, user),
        ConsoleEvalCaseSavedSchema,
        { method: 'POST' },
      )
    ).eval_case_id,

  getToolHealth: (tenant, range) =>
    callConsoleApi(
      `${CONSOLE_ROUTES.toolHealth(tenant)}${queryString(range ?? {})}`,
      ConsoleToolHealthSchema,
    ),
};

function queryString(fields: Readonly<Record<string, string | number | undefined>>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

function mcpServerRow(server: ConsoleMcpServer): McpServerRow {
  return {
    name: server.name,
    title: server.title,
    url: server.url,
    enabled: server.enabled,
    credential: server.credential,
  };
}

function mcpServerCreateRequest(server: NewMcpServer) {
  return {
    name: server.name,
    title: server.title,
    url: server.url,
    connect_with: server.connectWith,
    ...(server.clientId !== undefined ? { client_id: server.clientId } : {}),
    ...(server.clientSecret !== undefined ? { client_secret: server.clientSecret } : {}),
  };
}

function apiKeyRow(key: ConsoleApiKey): ApiKeyRow {
  return {
    id: key.id,
    kind: key.kind,
    mode: key.key_mode,
    createdAt: key.created_at,
    revokedAt: key.revoked_at,
    publishableKey: key.publishable_key,
  };
}

function toolRow(tool: ConsoleTool): ToolRow {
  return {
    source: tool.source,
    kind: tool.kind,
    sourceEnabled: tool.source_enabled,
    name: tool.name,
    title: tool.title,
    sourceDescription: tool.source_description,
    description: tool.description,
    descriptionReplaced: tool.description_replaced,
    enabled: tool.enabled,
    lastSeenAt: tool.last_seen_at,
  };
}
